// Toast — ephemeral notification. Sibling of `Banner` (§5.3), sharing its pill
// recipe but not its lifetime: banners persist until dismissed, toasts expire.
// Neutral by default; only failure states take a health treatment (§3).
//
// The stack is a bottom-anchored deck. Collapsed, rows sit behind the newest
// with a peek of each one showing; hovering or focusing the stack expands it to
// full height. Rows are absolutely positioned and carry their own offset, so a
// dismissal re-lays the survivors out and they travel into place rather than
// snapping.
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type CSSProperties } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { CircleCheck, CircleAlert, TriangleAlert, Info, LoaderCircle } from 'lucide-react';
import { Button } from '@/design-system/primitives/Button';

export type ToastKind = 'success' | 'error' | 'warning' | 'info' | 'loading';

export interface ToastOptions {
  description?: string;
  /** Milliseconds. `Infinity` holds the toast until it is dismissed by id. */
  duration?: number;
  /** Reusing an id replaces the toast already showing under it. */
  id?: string;
  /**
   * At most one, and never the only route to the action (§11.0). The slot is
   * icon-only, so the icon is required and the label carries the meaning — it
   * is the tooltip, the accessible name, and the screen-reader alternative.
   */
  action?: { label: string; icon: string; onClick: () => void };
}

interface ToastRecord extends ToastOptions {
  id: string;
  kind: ToastKind;
  message: string;
  closing?: boolean;
}

const DEFAULT_DURATION_MS = 4500;
const DISMISS_DURATION_MS = 320;

const GAP = 10;
/** How much of each covered row shows below the one in front of it. */
const PEEK = 10;
const SCALE_STEP = 0.05;
/** Rows deeper than this are not drawn while the deck is collapsed. */
const MAX_COLLAPSED_DEPTH = 2;

let records: ToastRecord[] = [];
const listeners = new Set<() => void>();
let sequence = 0;

function emit() {
  listeners.forEach((l) => l());
}

function push(kind: ToastKind, message: string, options?: ToastOptions): string {
  const id = options?.id ?? `ds-toast-${++sequence}`;
  const record: ToastRecord = { ...options, id, kind, message };
  const existing = records.findIndex((r) => r.id === id);
  records = existing === -1
    ? [...records, record]
    : records.map((r, i) => (i === existing ? record : r));
  emit();
  return id;
}

function remove(id?: string) {
  records = id === undefined ? [] : records.filter((r) => r.id !== id);
  emit();
}

/** Marks the row closing so its swoop can play, then drops it. */
function startClose(id: string) {
  const target = records.find((r) => r.id === id);
  if (!target || target.closing) return;
  records = records.map((r) => (r.id === id ? { ...r, closing: true } : r));
  emit();
  setTimeout(() => remove(id), DISMISS_DURATION_MS);
}

/**
 * Raise an ephemeral notification. Calling `toast` directly is informational;
 * the named methods carry the semantics.
 */
export const toast = Object.assign(
  (message: string, options?: ToastOptions) => push('info', message, options),
  {
    success: (message: string, options?: ToastOptions) => push('success', message, options),
    error: (message: string, options?: ToastOptions) => push('error', message, options),
    warning: (message: string, options?: ToastOptions) => push('warning', message, options),
    info: (message: string, options?: ToastOptions) => push('info', message, options),
    loading: (message: string, options?: ToastOptions) => push('loading', message, options),
    /** Dismisses one toast by id, or every toast when called with no argument. */
    dismiss: (id?: string) => {
      if (id === undefined) records.forEach((r) => startClose(r.id));
      else startClose(id);
    },
  },
);

interface KindStyle {
  icon: React.ComponentType<{ style?: CSSProperties; className?: string }>;
  shellBg: string;
  shellBorder: string;
  iconFg: string;
  iconChipBg?: string;
  textFg: string;
}

const NEUTRAL = {
  shellBg: 'var(--ds-glass-popover-fill)',
  shellBorder: 'var(--ds-control-border)',
  textFg: 'hsl(var(--ds-text-heading))',
} as const;

// Health tint matches Banner's: the *-bg token at /0.5, with a border mixed from
// the foreground hue.
const tinted = (family: 'red' | 'amber') => ({
  shellBg: `hsl(var(--ds-health-${family}-bg) / 0.5)`,
  shellBorder: `color-mix(in srgb, hsl(var(--ds-health-${family}-fg)) 32%, transparent)`,
  iconFg: `hsl(var(--ds-health-${family}-fg))`,
  textFg: `hsl(var(--ds-health-${family}-fg))`,
});

const KIND_STYLES: Record<ToastKind, KindStyle> = {
  success: { icon: CircleCheck, ...NEUTRAL, iconFg: 'hsl(var(--ds-health-green-fg))', iconChipBg: 'hsl(var(--ds-health-green-bg))' },
  info: { icon: Info, ...NEUTRAL, iconFg: 'hsl(var(--ds-info-blue-fg))', iconChipBg: 'hsl(var(--ds-info-blue-bg))' },
  loading: { icon: LoaderCircle, ...NEUTRAL, iconFg: 'hsl(var(--ds-brand-purple-fg))', iconChipBg: 'hsl(var(--ds-brand-purple-bg))' },
  warning: { icon: TriangleAlert, ...tinted('amber') },
  error: { icon: CircleAlert, ...tinted('red') },
};

interface RowLayout {
  y: number;
  scale: number;
  hidden: boolean;
}

function ToastRow({
  record,
  layout,
  paused,
  onMeasure,
}: {
  record: ToastRecord;
  layout: RowLayout;
  paused: boolean;
  onMeasure: (id: string, height: number) => void;
}) {
  const v = KIND_STYLES[record.kind];
  const Icon = v.icon;
  const duration = record.duration ?? DEFAULT_DURATION_MS;

  // Timing is ours, not Radix's. Radix keys its pause on a single ref shared by
  // every toast in the provider, and that ref is sticky: a toast mounting while
  // it is set never starts a timer at all, and only a later resume — leaving the
  // stack, or the window regaining focus — starts the clock. Since the pointer
  // is often already over the stack when the next toast arrives, that read as
  // "the timer doesn't start until you hover". Our pause is `paused`, the same
  // state that expands the deck, so the two cannot disagree.
  const remainingRef = useRef(duration);
  useEffect(() => {
    if (paused || record.closing || duration === Infinity) return;
    const startedAt = Date.now();
    const timer = window.setTimeout(() => startClose(record.id), Math.max(remainingRef.current, 0));
    return () => {
      window.clearTimeout(timer);
      remainingRef.current -= Date.now() - startedAt;
    };
  }, [paused, record.closing, record.id, duration]);

  // The enter keyframe fills forwards, so its final transform and opacity would
  // outrank the inline ones and freeze the row out of the restack transition.
  // Drop the animation once it has played and let the inline values govern.
  const [entering, setEntering] = useState(true);
  const ref = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(() => onMeasure(record.id, el.offsetHeight));
    observer.observe(el);
    onMeasure(record.id, el.offsetHeight);
    return () => observer.disconnect();
  }, [record.id, onMeasure]);

  return (
    <ToastPrimitive.Root
      ref={ref}
      // Held open on purpose. Left uncontrolled, Radix unmounts the row the
      // instant its timer fires — before the `closing` re-render can apply the
      // dismiss animation — so the toast vanished rather than swooping.
      open
      // Suppresses Radix's own timer (see the effect above); this still fires
      // for the close button, Escape, and swipe.
      duration={Infinity}
      onOpenChange={(open) => { if (!open) startClose(record.id); }}
      className="ds-toast font-body"
      style={{
        position: 'absolute',
        insetInline: 0,
        bottom: 0,
        display: 'flex', alignItems: 'center', gap: 10,
        boxSizing: 'border-box',
        padding: '8px 10px',
        borderRadius: 'var(--ds-radius-pill)',
        background: v.shellBg,
        backdropFilter: 'var(--ds-glass-popover-blur)',
        WebkitBackdropFilter: 'var(--ds-glass-popover-blur)',
        border: `1px solid ${v.shellBorder}`,
        boxShadow: 'var(--ds-shadow-md)',
        transformOrigin: 'bottom center',
        // Read by both keyframes so an entering or leaving row travels from its
        // own place in the deck, not from the anchor.
        ['--ds-toast-y' as string]: `${layout.y}px`,
        ['--ds-toast-scale' as string]: `${layout.scale}`,
        transform: `translateY(${layout.y}px) scale(${layout.scale})`,
        opacity: layout.hidden ? 0 : 1,
        pointerEvents: layout.hidden ? 'none' : 'auto',
        animation: record.closing
          ? `ds-toast-dismiss ${DISMISS_DURATION_MS}ms var(--ds-ease-knob) both`
          : entering ? 'ds-toast-in 0.22s var(--ds-ease-soft) both' : undefined,
      }}
      onAnimationEnd={() => { if (!record.closing) setEntering(false); }}
    >
      {v.iconChipBg ? (
        <span
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            width: 26, height: 26, borderRadius: '50%', background: v.iconChipBg,
          }}
        >
          <Icon
            className={record.kind === 'loading' ? 'ds-toast-spinner animate-spin' : undefined}
            style={{ width: 13, height: 13, color: v.iconFg }}
          />
        </span>
      ) : (
        <Icon style={{ width: 16, height: 16, flexShrink: 0, color: v.iconFg }} />
      )}

      {/* Block flow, as in Banner: message and description wrap together as one
          paragraph rather than truncating. */}
      <div style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.5 }}>
        <ToastPrimitive.Title asChild>
          <span className="font-display" style={{ fontWeight: 700, color: v.textFg }}>{record.message}</span>
        </ToastPrimitive.Title>
        {record.description && (
          <ToastPrimitive.Description asChild>
            <span style={{ color: v.textFg, marginLeft: 6 }}>{record.description}</span>
          </ToastPrimitive.Description>
        )}
      </div>

      {/* Its own slot, not trailing the sentence. Banner runs its action inline
          because a banner is a paragraph the reader is meant to finish; a toast
          is a notice with a control on it, and an underlined word at the end of
          the message reads as part of the message.
          `title` rather than a Tooltip: the design system has no tooltip
          primitive yet (#267), and the one in ui/ is on legacy tokens. */}
      {record.action && (
        <ToastPrimitive.Action asChild altText={record.action.label}>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            icon={record.action.icon}
            aria-label={record.action.label}
            title={record.action.label}
            onClick={record.action.onClick}
            style={{ flexShrink: 0 }}
          />
        </ToastPrimitive.Action>
      )}

      <ToastPrimitive.Close asChild>
        <Button variant="ghost" size="sm" iconOnly icon="x" aria-label="Dismiss" title="Dismiss" style={{ flexShrink: 0 }} />
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

/**
 * Mount once, at the application root. Bottom-right so the stack clears
 * `BannerStack` (top) and, at desktop widths, `FloatingDock` (bottom centre);
 * above `Confirm`'s 210 so a failure raised from inside a dialog is still seen.
 * Below roughly 780px the viewport's `maxWidth: calc(100vw - 40px)` and the
 * dock's own near-full width overlap, and the toast wins on z-index — deferred
 * with the rest of the mobile pass.
 */
export function Toaster() {
  const items = useSyncExternalStore(subscribe, () => records, () => records);
  const [expanded, setExpanded] = useState(false);
  const [heights, setHeights] = useState<Record<string, number>>({});

  const onMeasure = useCallback((id: string, height: number) => {
    setHeights((prev) => (prev[id] === height ? prev : { ...prev, [id]: height }));
  }, []);

  useEffect(() => {
    if (items.length === 0 && expanded) setExpanded(false);
  }, [items.length, expanded]);

  // Radix portals each row into the viewport, so in the React tree the rows are
  // the viewport's siblings and synthetic enter/leave never reaches it. Native
  // pointerover/focusin do bubble, because the DOM parentage is real.
  const viewportRef = useRef<HTMLOListElement | null>(null);
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const open = () => setExpanded(true);
    const close = (e: PointerEvent | FocusEvent) => {
      if (el.contains(e.relatedTarget as Node | null)) return;
      setExpanded(false);
    };
    el.addEventListener('pointerover', open);
    el.addEventListener('pointerout', close as EventListener);
    el.addEventListener('focusin', open);
    el.addEventListener('focusout', close as EventListener);
    return () => {
      el.removeEventListener('pointerover', open);
      el.removeEventListener('pointerout', close as EventListener);
      el.removeEventListener('focusin', open);
      el.removeEventListener('focusout', close as EventListener);
    };
  }, []);

  const front = items.length - 1;
  // Offsets accumulate from the anchored edge: the newest row sits on the
  // corner, each older one above the rows in front of it.
  const layouts: RowLayout[] = items.map((record, i) => {
    const depth = front - i;
    if (expanded) {
      let y = 0;
      for (let j = i + 1; j <= front; j++) y += (heights[items[j].id] ?? 0) + GAP;
      return { y: -y, scale: 1, hidden: false };
    }
    return {
      y: -depth * PEEK,
      scale: Math.max(1 - depth * SCALE_STEP, 0.85),
      hidden: depth > MAX_COLLAPSED_DEPTH,
    };
  });

  const frontHeight = items.length ? (heights[items[front].id] ?? 0) : 0;
  const viewportHeight = items.length === 0 ? 0
    : expanded
      ? items.reduce((total, r) => total + (heights[r.id] ?? 0) + GAP, 0) - GAP
      : frontHeight + Math.min(front, MAX_COLLAPSED_DEPTH) * PEEK;

  return (
    <ToastPrimitive.Provider swipeDirection="right">
      {items.map((record, i) => (
        <ToastRow key={record.id} record={record} layout={layouts[i]} paused={expanded} onMeasure={onMeasure} />
      ))}
      <ToastPrimitive.Viewport
        ref={viewportRef}
        className="ds-toast-viewport"
        style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 300,
          width: 380, maxWidth: 'calc(100vw - 40px)',
          height: viewportHeight,
          margin: 0, padding: 0, listStyle: 'none', outline: 'none',
          pointerEvents: items.length ? 'auto' : 'none',
        }}
      />
    </ToastPrimitive.Provider>
  );
}
